import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore.js';
import { useSyncStore } from '../../stores/syncStore.js';
import { SyncStatus, SyncStatusState } from '../ui/SyncStatus.js';
import { useToast } from '../ui/useToast.js';
import { Avatar } from '../ui/Avatar.js';

import { useTopBarStore } from '../../stores/topBarStore.js';
import { useUnseenEdits } from '../../hooks/useUnseenEdits.js';

export interface TopBarProps {
  title?: string;
  showBack?: boolean;
  backTo?: string;
  onBack?: () => void;
  syncState?: SyncStatusState;
  queuedSyncCount?: number;
  onSyncClick?: () => void;
  onSearchClick?: () => void;
  onAccountClick?: () => void;
  actions?: React.ReactNode;
  className?: string;
  showLogo?: boolean;
  hideDefaultActions?: boolean;
}

export const TopBar: React.FC<TopBarProps> = ({
  title,
  showBack = false,
  backTo,
  onBack,
  syncState,
  queuedSyncCount,
  onSyncClick,
  onSearchClick,
  onAccountClick,
  actions,
  className = '',
  showLogo,
  hideDefaultActions
}) => {
  const store = useTopBarStore();
  const effectiveTitle = store.title ?? title ?? 'خانه';
  const effectiveShowLogo = showLogo !== undefined ? showLogo : (store.showLogo ?? true);
  const effectiveHideDefaultActions =
    hideDefaultActions !== undefined ? hideDefaultActions : (store.hideDefaultActions ?? false);
  const effectiveActions = actions ?? store.actions;
  const effectiveShowBack = showBack || (store.showBack ?? false);
  const effectiveOnBack = onBack ?? store.onBack;
  const effectiveBackTo = backTo ?? store.backTo;
  const effectiveBackLabel = store.backLabel ?? 'بازگشت';
  const navigate = useNavigate();
  const { user, isGuest } = useAuthStore();
  const hasUnseen = useUnseenEdits();
  const toast = useToast();
  const {
    status: storeStatus,
    queuedSessionsCount,
    dirtyRoutinesCount,
    triggerSync
  } = useSyncStore();

  const totalQueued = queuedSessionsCount + dirtyRoutinesCount;
  const effectiveState = syncState !== undefined && onSyncClick !== undefined ? syncState : storeStatus;
  const effectiveQueued = queuedSyncCount !== undefined && onSyncClick !== undefined ? queuedSyncCount : totalQueued;

  const handleManualSync = async () => {
    if (onSyncClick) {
      onSyncClick();
      return;
    }
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      toast.warning('دستگاه در حالت آفلاین است.');
      return;
    }
    const res = await triggerSync();
    if (res.success) {
      toast.success('تغییرات با موفقیت همگام شد.');
    } else {
      toast.error(res.message || 'خطا در برقراری ارتباط با سرور');
    }
  };

  const handleBack = () => {
    if (effectiveOnBack) {
      effectiveOnBack();
    } else if (effectiveBackTo) {
      navigate(effectiveBackTo);
    } else {
      navigate(-1);
    }
  };

  const displayName = isGuest ? 'کاربر مهمان' : user?.fullName || 'کاربر';

  return (
    <header
      data-testid="mobile-topbar"
      className={`lg:hidden flex items-center justify-between h-14 min-h-14 px-4 gap-2.5 sticky top-0 bg-base-100 border-b border-base-300 z-40 select-none ${className}`}
    >
      {/* Start: Back or Logo + Title */}
      <div className="flex items-center gap-2.5 min-w-0 flex-1">
        {effectiveShowBack ? (
          <button
            type="button"
            onClick={handleBack}
            aria-label={effectiveBackLabel}
            className="size-11 -ms-2 inline-flex items-center justify-center rounded-xl text-base-content/80 hover:text-base-content active:scale-95 transition cursor-pointer"
          >
            <ChevronRight className="size-6" />
          </button>
        ) : effectiveShowLogo ? (
          <img
            src="/icon.svg"
            alt="FretBoard"
            className="size-[30px] rounded-lg object-cover shrink-0"
          />
        ) : null}
        {store.subtitle ? (
          <div className="flex flex-col min-w-0">
            {store.editableTitle ? (
              <input
                type="text"
                dir="auto"
                value={store.editableTitle.value}
                maxLength={store.editableTitle.maxLength}
                onChange={(e) => store.editableTitle?.onChange(e.target.value)}
                onBlur={store.editableTitle.onBlur}
                aria-label="نام روتین"
                data-testid="routine-title-input"
                className="w-full min-w-0 bg-transparent text-base font-bold text-base-content truncate tracking-tight border border-transparent focus:border-primary rounded-lg px-1 -mx-1 outline-none"
              />
            ) : (
              <h1 className="text-base font-bold text-base-content truncate tracking-tight">
                <bdi dir="auto">{effectiveTitle}</bdi>
              </h1>
            )}
            <p
              data-testid={store.editableTitle ? 'routine-autosave-indicator' : undefined}
              className="text-[11px] text-base-content/60 leading-tight"
            >
              <bdi dir="auto">{store.subtitle}</bdi>
            </p>
          </div>
        ) : (
          <h1 className="text-lg font-bold text-base-content truncate tracking-tight">
            <bdi dir="auto">{effectiveTitle}</bdi>
          </h1>
        )}
      </div>

      {/* End: Custom Actions, SyncStatus, Search, Avatar */}
      <div className="flex items-center gap-2.5 shrink-0">
        {effectiveActions}

        {/* FND-37: the sync indicator stays on every shell route, even when a page replaces the default actions. */}
        <SyncStatus
          compact
          state={effectiveState}
          queuedCount={effectiveQueued}
          onClick={handleManualSync}
          data-testid="sync-status-compact"
        />

        {!effectiveHideDefaultActions && (
          <>
            <button
              type="button"
              data-testid="global-search-trigger"
              onClick={onSearchClick}
              aria-label="جستجو"
              className="size-11 inline-flex items-center justify-center rounded-xl text-base-content/70 hover:text-base-content hover:bg-base-300/60 transition active:scale-95 cursor-pointer"
            >
              <Search className="size-5" />
            </button>

            <button
              type="button"
              data-testid="account-menu-trigger"
              onClick={onAccountClick}
              aria-label="حساب کاربری"
              className="relative size-11 inline-flex items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 ring-primary transition active:scale-95 cursor-pointer"
            >
              <Avatar name={displayName} size="sm" />
              {hasUnseen && (
                <span
                  data-testid="account-unseen-dot"
                  className="absolute top-1 end-1 size-2 rounded-full bg-primary ring-2 ring-base-100"
                  aria-hidden="true"
                />
              )}
            </button>
          </>
        )}
      </div>
    </header>
  );
};

TopBar.displayName = 'TopBar';
